import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {useTabHashPersistence} from "../../../../hooks/useTabHashPersistence";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {OrderRequestsTabPane} from "./components/OrderRequestsTabPane";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import Button from "antd/es/button/button";
import {Badge, message} from "antd";
import {OrderRequestsFilterModal} from "./components/OrderRequestsFilterModal";
import FilterIcon from "../../../../media/icons/filter.svg?react";
import {
  clearArchiveOrderRequest,
  clearChangeOrderRequestWithdraw,
  clearUpdateOrderRequestFavorite,
  getFreighterOrderRequests,
  getOrderRequestListCounts,
} from "../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducer";
import {renderSuccessModal} from "../../../../components/UI/StatusModals/renderSuccessModal";
import {defineMessages, useIntl} from "react-intl";
import {readNotifications} from "../../../../store/reducers/notificationsReducer/notificationsReducer";
import {NotificationType} from "../../../../store/reducers/notificationsReducer/notificationsReducerTypes";
import {useSearchParams} from "react-router-dom";
import {OrderTypeEnum} from "../../../../store/reducers/chartererRequestsReducer/chartererRequestsReducerTypes";
import {CustomSelect} from "../../../../components/UI/AntCustom/CustomSelect";
import {useIsMobile} from "../../../../hooks/useWindowSize";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {loadCurrencyRates} from "../../../../utils/helpers/currencyConverter/currencyConverter";


const RequestFromAgentsPageMessages = defineMessages({
  active: {
    id: "requestFromAgentsPage.active",
    defaultMessage: "Active",
  },
  archival: {
    id: "requestFromAgentsPage.archival",
    defaultMessage: "Archive",
  },
  favorites: {
    id: "requestFromAgentsPage.favorites",
    defaultMessage: "Favorites",
  },
  changeRespondStatusSuccess: {
    id: "requestFromAgentsPage.changeRespondStatusSuccess",
    defaultMessage: "Respond status has been successfully updated.",
  },
  archiveRespondSuccess: {
    id: "requestFromAgentsPage.archiveRespondSuccess",
    defaultMessage: "The respond has been successfully archived.",
  },
  title: {
    id: "requestFromAgentsPage.title",
    defaultMessage: "My offers",
  },
})

export interface IOrderRequestFilters {
  airportFromId?: number;
  airportToId?: number;
  minPax?: number;
  maxPax?: number;
  companionsAllowed?: boolean;
  isEmptyLegUsable?: boolean;
  planeCategoryIds?: number[];
  onlyApproved?: boolean; // Для фильтра "Только принятые запросы" (используется в брокере)
}

enum RequestFromAgentsTabKeysEnum {
  ACTIVE = 'active',
  ARCHIVE = 'archive',
  FAVORITES = 'favorites',
}

interface IContentList {
  active: ReactNode;
  archive: ReactNode;
  favorites: ReactNode;
}

export const RequestFromAgentsPage = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const [orderFilterTypes, setOrderFilterTypes] = useState<OrderTypeEnum[]>([]);
  const [activeTabKey, setActiveTabKey] = useTabHashPersistence(
    Object.values(RequestFromAgentsTabKeysEnum),
    searchParams.get('archived') ? RequestFromAgentsTabKeysEnum.ARCHIVE : RequestFromAgentsTabKeysEnum.ACTIVE
  );
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<IOrderRequestFilters>({})
  const {
    isLoading: isChangeStatusLoading,
    isSuccess: isChangeStatusSuccess,
    errorMessage: changeStatusErrorMessage,
  } = useAppSelector(state => state.freighterRequests.changeOrderRequestWithdraw);
  const {
    isLoading: isArchiveLoading,
    isSuccess: isArchiveSuccess,
    errorMessage: archivesErrorMessage,
  } = useAppSelector(state => state.freighterRequests.archiveOrderRequest);
  const unreadNotifications = useAppSelector(state => state.notifications.itemsObject.items)
    .filter(n => !n.isRead)
    .filter(n => [NotificationType.TYPE_ORDER_REQUEST_CREATED, NotificationType.TYPE_ORDER_REQUEST_EXPIRED, NotificationType.TYPE_ORDER_ROUTE_ARCHIVED].includes(n.type))

  const orderRequestListCounts = useAppSelector(state => state.freighterRequests.orderRequestListCounts);
  const updateOrderRequestFavorite = useAppSelector(state => state.freighterRequests.updateOrderRequestFavorite);
  
  // Update favorites count when user changes isFavorite status
  useEffect(() => {
    if (updateOrderRequestFavorite.isSuccess) {
      // Reload counts after favorite status change
      dispatch(getOrderRequestListCounts({
        orderTypes: orderFilterTypes,
        filters: filters,
      }));
      
      // If user is on favorites tab and removed item from favorites, reload the list
      if (activeTabKey === RequestFromAgentsTabKeysEnum.FAVORITES) {
        dispatch(getFreighterOrderRequests({
          page: 1,
          isArchival: false,
          orderTypes: orderFilterTypes,
          filters: filters,
          isFavorite: true,
        }));
      }
      
      dispatch(clearUpdateOrderRequestFavorite());
    }
  }, [updateOrderRequestFavorite.isSuccess, activeTabKey, orderFilterTypes, filters, dispatch])

  const tabList = useMemo(() => [
    {key: RequestFromAgentsTabKeysEnum.ACTIVE, tab: `${intl.formatMessage(RequestFromAgentsPageMessages.active)}(${orderRequestListCounts.active})`},
    {key: RequestFromAgentsTabKeysEnum.ARCHIVE, tab: `${intl.formatMessage(RequestFromAgentsPageMessages.archival)}(${orderRequestListCounts.archival})`},
    {key: RequestFromAgentsTabKeysEnum.FAVORITES, tab: `${intl.formatMessage(RequestFromAgentsPageMessages.favorites)}(${orderRequestListCounts.favorites})`},
  ], [intl, orderRequestListCounts]);

  useEffect(() => {
    if (!isChangeStatusLoading) {
      if (isChangeStatusSuccess) {
        renderSuccessModal(intl.formatMessage(RequestFromAgentsPageMessages.changeRespondStatusSuccess));
        dispatch(getOrderRequestListCounts({orderTypes: orderFilterTypes, filters: filters}));
        dispatch(getFreighterOrderRequests({
          page: 1,
          isArchival: activeTabKey === RequestFromAgentsTabKeysEnum.ARCHIVE,
          orderTypes: orderFilterTypes,
          filters: filters,
          isFavorite: activeTabKey === RequestFromAgentsTabKeysEnum.FAVORITES,
        }));
        dispatch(clearChangeOrderRequestWithdraw());
      }
      if (changeStatusErrorMessage)
        message.error(changeStatusErrorMessage);
    }
  }, [isChangeStatusLoading, isChangeStatusSuccess, changeStatusErrorMessage, activeTabKey, orderFilterTypes, filters, dispatch])

  useEffect(() => {
    if (!isArchiveLoading) {
      if (isArchiveSuccess) {
        renderSuccessModal(intl.formatMessage(RequestFromAgentsPageMessages.archiveRespondSuccess));
        dispatch(getOrderRequestListCounts({orderTypes: orderFilterTypes, filters: filters}));
        dispatch(getFreighterOrderRequests({
          page: 1,
          isArchival: activeTabKey === RequestFromAgentsTabKeysEnum.ARCHIVE,
          orderTypes: orderFilterTypes,
          filters: filters,
          isFavorite: activeTabKey === RequestFromAgentsTabKeysEnum.FAVORITES,
        }));
        dispatch(clearArchiveOrderRequest());
      }
      if (archivesErrorMessage)
        message.error(archivesErrorMessage);
    }
  }, [isArchiveLoading, isArchiveSuccess, archivesErrorMessage, activeTabKey, orderFilterTypes, filters, dispatch])

  useEffect(() => {
    readNewNotifications()
  }, [unreadNotifications])

  const readNewNotifications = () => {
    if (unreadNotifications.length > 0)
      dispatch(readNotifications({notificationIds: unreadNotifications.map(n => n.id)}))
  }

  // Load counts on mount and when filters/types change
  useEffect(() => {
    dispatch(getOrderRequestListCounts({
      orderTypes: orderFilterTypes,
      filters: filters,
    }));
  }, [orderFilterTypes, filters, dispatch])

  // Preload currency rates once for all order cards (modals/drawers need them)
  useEffect(() => {
    loadCurrencyRates();
  }, [])

  const contentList: IContentList = {
    active: <OrderRequestsTabPane orderTypes={orderFilterTypes} filters={filters} expectedCount={orderRequestListCounts.active}/>,
    archive: <OrderRequestsTabPane orderTypes={orderFilterTypes} filters={filters} isArchival={true} expectedCount={orderRequestListCounts.archival}/>,
    favorites: <OrderRequestsTabPane orderTypes={orderFilterTypes} filters={filters} isFavorite={true} expectedCount={orderRequestListCounts.favorites}/>,
  };

  const extra = <>
    <CustomSelect mode={"tags"} defaultValue={[]}
                  placement={'bottomRight'}
                  style={{minWidth: isMobile ? 88 : 120, width: isMobile ? 88 : "unset"}}
                  showArrow={true}
                  allowClear={true}
                  dropdownMatchSelectWidth={false}
                  onChange={(types) => setOrderFilterTypes((types as string[]).map(Number) as OrderTypeEnum[])} placeholder={"All"} options={[
      {label: "One way", value: String(OrderTypeEnum.ONE_WAY)},
      {label: "Round Trip", value: String(OrderTypeEnum.ROUND_TRIP)},
      {label: "Multi Leg", value: String(OrderTypeEnum.MULTI_LEG)},
    ]}/>
    <Badge count={Object.values(filters).filter(v => v).length}>
      <CustomButton onClick={() => setIsFiltersModalOpen(true)} heightSize={'m'} style={{marginLeft: 8}}>
        <FilterIcon/>
      </CustomButton>
    </Badge>
  </>

  return (
    <PageWrapper>
      <CustomCard title={intl.formatMessage(RequestFromAgentsPageMessages.title)} width={'max'} isContainerCard={true}
                  extra={isMobile && extra}
                  tabList={tabList}
                  tabBarExtraContent={!isMobile && extra}
                  defaultActiveTabKey={activeTabKey}
                  onTabChange={(key: string) => {
                    setActiveTabKey(key as RequestFromAgentsTabKeysEnum);
                  }}>
        {contentList[activeTabKey as keyof typeof contentList]}
      </CustomCard>
      {isFiltersModalOpen &&
        <OrderRequestsFilterModal isModalVisible={isFiltersModalOpen} setIsModalVisible={setIsFiltersModalOpen}
                                  filters={filters} setFilters={setFilters}/>}
    </PageWrapper>
  );
};

